import {
    Button,
    Checkbox,
    type ComboboxData,
    Group,
    type MantineSize,
    Stack,
    Tabs,
    Text,
} from '@mantine/core';
import { randomId } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { type ReactElement, type ReactNode, useState } from 'react';
import { Show } from '../../components/control-flow/Show';
import { Controlled_RichText } from '../../components/inputs/controlled/Controlled_RichText';
import { ValidationEntrySeverity } from '../../components/ValidationEntrySeverity';
import type { Enum } from '../../types/enum';
import { ControlledInputs } from '../read-only-inputs/controlledInputs';
import { isFunction, isObject, isString } from '../utilFunctions';

const StringInputContent = ({
    onConfirm,
    onCancel,
    id,
    label,
    withMultiLineInput,
    multiLineLabel,
    width,
    multiLineAsRichtext,
}: {
    onConfirm: (value: string, multiLineValue?: string) => void;
    onCancel?: () => void;
    id: string;
    label?: string;
    withMultiLineInput?: boolean;
    multiLineLabel?: string;
    width?: number;
    multiLineAsRichtext?: boolean;
}) => {
    const [valueState, setValueState] = useState('');
    const [multiLineValueState, setMultiLineValueState] = useState('');
    const handleConfirm = () => {
        if (withMultiLineInput) {
            onConfirm(valueState, multiLineValueState);
        } else {
            onConfirm(valueState);
        }
        modals.close(id);
    };

    return (
        <Stack
            miw={width ?? 400}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    handleConfirm();
                }
            }}
        >
            <ControlledInputs.String
                w={'100%'}
                readOnly={false}
                nullable={false}
                value={valueState}
                onChange={setValueState}
                label={label}
            />
            <Show when={withMultiLineInput && !multiLineAsRichtext}>
                <ControlledInputs.MultilineString
                    nullable={false}
                    value={multiLineValueState}
                    onChange={setMultiLineValueState}
                    label={multiLineLabel}
                    readOnly={false}
                    onKeyUp={(event) => {
                        event.stopPropagation();
                    }}
                />
            </Show>
            <Show when={withMultiLineInput && multiLineAsRichtext}>
                <div>
                    {multiLineLabel !== undefined && (
                        <Text fz={14}>{multiLineLabel}</Text>
                    )}
                    <Controlled_RichText
                        value={multiLineValueState}
                        onChange={setMultiLineValueState}
                        readonly={false}
                    />
                </div>
            </Show>
            <Group justify="flex-end">
                <Button
                    variant="default"
                    onClick={() => {
                        if (onCancel) {
                            onCancel();
                        }
                        modals.close(id);
                    }}
                >
                    Cancel
                </Button>
                <Button onClick={handleConfirm}>Confirm</Button>
            </Group>
        </Stack>
    );
};

const DoubleStringInputContent = ({
    firstLabel,
    secondLabel,
    onConfirm,
    onCancel,
    id,
    width,
}: {
    firstLabel: string;
    secondLabel: string;
    onConfirm: (firstValue: string, secondValue: string | null) => void;
    onCancel?: () => void;
    id: string;
    width?: number;
}) => {
    const [firstValueState, setFirstValueState] = useState<string>('');
    const [secondValueState, setSecondValueState] = useState<string | null>(
        null
    );

    const handleConfirm = () => {
        onConfirm(firstValueState, secondValueState);
        modals.close(id);
    };

    return (
        <Stack
            miw={width ?? 400}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    handleConfirm();
                }
            }}
        >
            <ControlledInputs.String
                w={'100%'}
                readOnly={false}
                nullable={false}
                value={firstValueState}
                onChange={setFirstValueState}
                label={firstLabel}
            />
            <ControlledInputs.String
                w={'100%'}
                readOnly={false}
                nullable={true}
                value={secondValueState}
                onChange={setSecondValueState}
                label={secondLabel}
            />
            <Group justify="flex-end">
                <Button
                    variant="default"
                    onClick={() => {
                        if (onCancel) {
                            onCancel();
                        }
                        modals.close(id);
                    }}
                >
                    Cancel
                </Button>
                <Button onClick={handleConfirm}>Confirm</Button>
            </Group>
        </Stack>
    );
};

const SelectionInputContent = ({
    onConfirm,
    initialSelection,
    searchable,
    data,
    onCancel,
    id,
    width,
    notNullable
}: {
    onConfirm: (value: string | null) => void;
    initialSelection: string | null;
    searchable: boolean;
    data: ComboboxData;
    onCancel?: () => void;
    id: string;
    width?: number;
    notNullable?: boolean
}) => {
    const [valueState, setValueState] = useState<string | null>(
        initialSelection
    );

    const handleConfirm = () => {
        onConfirm(valueState);
        modals.close(id);
    };
    return (
        <Stack
            miw={width ?? 400}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    handleConfirm();
                }
            }}
        >
            <ControlledInputs.Select
                readOnly={false}
                nullable={!(initialSelection && notNullable)}
                searchable={searchable}
                value={valueState}
                data={data}
                onChange={setValueState}
            />
            <Group justify="flex-end">
                <Button
                    variant="default"
                    onClick={() => {
                        if (onCancel) {
                            onCancel();
                        }
                        modals.close(id);
                    }}
                >
                    Cancel
                </Button>
                <Button onClick={handleConfirm}>Confirm</Button>
            </Group>
        </Stack>
    );
};

const SelectionInputWithBooleanContent = ({
    onConfirm,
    initialSelection,
    initialBoolean,
    searchable,
    data,
    selectLabel,
    booleanLabel,
    onCancel,
    id,
    width,
    notNullable,
    selectionXORBoolean
}: {
    onConfirm: (value: string | null, bool: boolean) => void;
    initialSelection: string | null;
    initialBoolean: boolean;
    searchable: boolean;
    data: ComboboxData;
    selectLabel: string;
    booleanLabel: string;
    onCancel?: () => void;
    id: string;
    width?: number;
    notNullable?: boolean
    selectionXORBoolean?: boolean
}) => {
    const [valueState, setValueState] = useState<string | null>(
        selectionXORBoolean && initialBoolean && !notNullable ? null : initialSelection
    );
    const [boolState, setBoolState] = useState<boolean>(
        initialBoolean
    );

    const handleConfirm = () => {
        onConfirm(valueState, boolState);
        modals.close(id);
    };
    return (
        <Stack
            miw={width ?? 400}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    handleConfirm();
                }
            }}
        >
            <ControlledInputs.BooleanSwitch
                readOnly={false}
                label={booleanLabel}
                value={boolState}
                onChange={(value) => {
                    setBoolState(value);
                    if (value && selectionXORBoolean && !notNullable) {
                        setValueState(null);
                    }
                }}
            />
            <ControlledInputs.Select
                readOnly={false}
                label={selectLabel}
                nullable={!(initialSelection && notNullable)}
                searchable={searchable}
                value={valueState}
                data={data}
                onChange={(value) => {
                    setValueState(value);
                    if (value && selectionXORBoolean) {
                        setBoolState(false);
                    }
                }}
            />
            <Group justify="flex-end">
                <Button
                    variant="default"
                    onClick={() => {
                        if (onCancel) {
                            onCancel();
                        }
                        modals.close(id);
                    }}
                >
                    Cancel
                </Button>
                <Button onClick={handleConfirm}>Confirm</Button>
            </Group>
        </Stack>
    );
};

const DoubleSelectionInputContent = ({
    onConfirm,
    searchable,
    firstLabel,
    secondLabel,
    firstData,
    secondData,
    onCancel,
    id,
    width,
}: {
    onConfirm: (firstValue: string | null, secondValue: string | null) => void;
    searchable: boolean;
    firstLabel: string;
    secondLabel: string;
    firstData: ComboboxData;
    secondData: ComboboxData | ((firstValue: string | null) => ComboboxData);
    onCancel?: () => void;
    id: string;
    width?: number;
}) => {
    const [firstValueState, setFirstValueState] = useState<string | null>(null);
    const [secondValueState, setSecondValueState] = useState<string | null>(
        null
    );

    const handleConfirm = () => {
        onConfirm(firstValueState, secondValueState);
        modals.close(id);
    };

    return (
        <Stack
            miw={width ?? 400}
            onKeyUp={(event) => {
                if (event.key === 'Enter') {
                    handleConfirm();
                }
            }}
        >
            <ControlledInputs.Select
                readOnly={false}
                nullable={true}
                searchable={searchable}
                value={firstValueState}
                label={firstLabel}
                data={firstData}
                onChange={(value) => {
                    setFirstValueState(value);
                    if (isFunction(secondData)) {
                        const newSecondData = secondData(value);
                        if (
                            !newSecondData.some(
                                (secondValue) =>
                                    (isObject(secondValue) &&
                                        'value' in secondValue &&
                                        secondValue.value ===
                                        secondValueState) ||
                                    (isString(secondValue) &&
                                        secondValue === secondValueState)
                            )
                        ) {
                            setSecondValueState(null);
                        }
                    }
                }}
            />
            <ControlledInputs.Select
                readOnly={false}
                nullable={true}
                searchable={searchable}
                value={secondValueState}
                label={secondLabel}
                data={
                    isFunction(secondData) ?
                        secondData(firstValueState)
                        :   secondData
                }
                onChange={setSecondValueState}
            />
            <Group justify="flex-end">
                <Button
                    variant="default"
                    onClick={() => {
                        if (onCancel) {
                            onCancel();
                        }
                        modals.close(id);
                    }}
                >
                    Cancel
                </Button>
                <Button onClick={handleConfirm}>Confirm</Button>
            </Group>
        </Stack>
    );
};

const openContentModal = ({
    title,
    content,
    id,
    size,
    zIndex,
}: {
    title: ReactNode;
    content: ReactNode;
    id?: string;
    size?: MantineSize | number;
    zIndex?: number;
}) => {
    if (zIndex) {
        modals.open({
            title,
            children: content,
            size: size ?? 'auto',
            modalId: id,
            styles: { header: { display: 'flex' }, title: { flexGrow: 1 } },
            zIndex,
        });
    } else {
        modals.open({
            title,
            children: content,
            size: size ?? 'auto',
            modalId: id,
            styles: { header: { display: 'flex' }, title: { flexGrow: 1 } },
        });
    }
};

const openAlertModal = (
    title: string,
    text: ReactElement | string,
    onConfirm: () => void,
    onCancel?: () => void,
    confirmButtonText?: string,
    cancelButtonText?: string
) => {
    modals.openConfirmModal({
        title,
        children:
            isString(text) ?
                <Text
                    style={{ whiteSpace: 'preserve-breaks' }}
                    size="sm"
                >
                    {text}
                </Text>
                :   text,
        labels: {
            confirm: confirmButtonText ?? 'Confirm',
            cancel: cancelButtonText ?? 'Cancel',
        },
        onConfirm,
        onCancel,
        closeOnClickOutside: false,
    });
};

const openUnsavedChangesAlertModal = (
    onConfirm: () => void,
    onCancel?: () => void
) => {
    openAlertModal(
        'Warning! Unsaved Changes',
        'You have unsaved changes in this view!\nAre you sure you want to proceed without saving?',
        onConfirm,
        onCancel
    );
};

const openStringInputModal = (
    title: string,
    onConfirm: (value: string, multiLineValue?: string) => void,
    onCancel?: () => void,
    label?: string,
    withMultiLineInput?: boolean,
    multiLineLabel?: string,
    width?: number,
    multiLineAsRichtext?: boolean
) => {
    const id = randomId();

    openContentModal({
        title,
        content: (
            <StringInputContent
                width={width}
                label={label}
                withMultiLineInput={withMultiLineInput}
                multiLineLabel={multiLineLabel}
                onConfirm={onConfirm}
                onCancel={onCancel}
                id={id}
                multiLineAsRichtext={multiLineAsRichtext}
            />
        ),
        id,
    });
};

const openEnumInputModal = <E extends string>(
    title: string,
    enumType: Enum<E>,
    initialEnum: E | null,
    enumFormatter: (value: E) => string,
    onConfirm: (value: E | null) => void,
    onCancel?: () => void,
    filter?: (value: string) => boolean,
    width?: number
) => {
    const id = randomId();
    const EnumInputContent = ({
        onConfirm,
        onCancel,
        id,
    }: {
        onConfirm: (value: E | null) => void;
        onCancel?: () => void;
        id: string;
    }) => {
        const [valueState, setValueState] = useState<E | null>(initialEnum);

        const handleConfirm = () => {
            onConfirm(valueState);
            modals.close(id);
        };

        return (
            <Stack
                miw={width ?? 400}
                onKeyUp={(event) => {
                    if (event.key === 'Enter') {
                        handleConfirm();
                    }
                }}
            >
                <ControlledInputs.Enum
                    readOnly={false}
                    nullable={true}
                    value={valueState}
                    enumType={enumType}
                    typeFilter={filter}
                    enumFormatter={enumFormatter}
                    onChange={setValueState}
                />
                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={() => {
                            if (onCancel) {
                                onCancel();
                            }
                            modals.close(id);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm}>Confirm</Button>
                </Group>
            </Stack>
        );
    };

    openContentModal({
        title,
        content: (
            <EnumInputContent
                onConfirm={onConfirm}
                onCancel={onCancel}
                id={id}
            />
        ),
        id,
    });
};

const openSelectionInputModal = (
    title: string,
    data: ComboboxData,
    searchable: boolean,
    initialSelection: string | null,
    onConfirm: (value: string | null) => void,
    onCancel?: () => void,
    width?: number,
    notNullable?: boolean
) => {
    const id = randomId();

    openContentModal({
        title,
        content: (
            <SelectionInputContent
                width={width}
                onConfirm={onConfirm}
                onCancel={onCancel}
                id={id}
                searchable={searchable}
                data={data}
                initialSelection={initialSelection}
                notNullable={notNullable}
            />
        ),
        id,
    });
};

const openSelectionInputWithBooleanModal = (
    title: string,
    data: ComboboxData,
    searchable: boolean,
    initialSelection: string | null,
    initialBoolean: boolean,
    onConfirm: (value: string | null, bool: boolean) => void,
    selectLabel: string,
    booleanLabel: string,
    onCancel?: () => void,
    width?: number,
    notNullable?: boolean,
    selectionXORBoolean?: boolean,
) => {
    const id = randomId();

    openContentModal({
        title,
        content: (
            <SelectionInputWithBooleanContent
                width={width}
                onConfirm={onConfirm}
                onCancel={onCancel}
                id={id}
                searchable={searchable}
                data={data}
                initialSelection={initialSelection}
                initialBoolean={initialBoolean}
                notNullable={notNullable}
                selectLabel={selectLabel}
                booleanLabel={booleanLabel}
                selectionXORBoolean={selectionXORBoolean}
            />
        ),
        id,
    });
};

const openSelectionOrCreateInputModal = (
    title: string,
    data: ComboboxData,
    searchable: boolean,
    initialSelection: string | null,
    onSelected: (value: string | null) => void,
    onCreated: (value: string, multiLineValue?: string) => void,
    onCancel?: () => void,
    label?: string,
    withMultiLineInput?: boolean,
    multiLineLabel?: string,
    width?: number
) => {
    const id = randomId();

    const tabs = () => {
        return (
            <Tabs defaultValue={'select'}>
                <Tabs.List>
                    <Tabs.Tab value={'select'}>Select</Tabs.Tab>
                    <Tabs.Tab value={'create'}>Create</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value={'select'}>
                    <SelectionInputContent
                        width={width}
                        onConfirm={onSelected}
                        onCancel={onCancel}
                        id={id}
                        searchable={searchable}
                        data={data}
                        initialSelection={initialSelection}
                    />
                </Tabs.Panel>
                <Tabs.Panel value={'create'}>
                    <StringInputContent
                        width={width}
                        label={label}
                        withMultiLineInput={withMultiLineInput}
                        multiLineLabel={multiLineLabel}
                        onConfirm={onCreated}
                        onCancel={onCancel}
                        id={id}
                    />
                </Tabs.Panel>
            </Tabs>
        );
    };

    openContentModal({ title, content: tabs(), id });
};

const openDoubleInputModal = (
    title: string,
    firstLabel: string,
    secondLabel: string,
    onCreated: (firstValue: string, secondValue: string | null) => void,
    onCancel?: () => void,
    width?: number
) => {
    const id = randomId();

    openContentModal({
        title,
        content: (
            <DoubleStringInputContent
                width={width}
                onConfirm={onCreated}
                onCancel={onCancel}
                id={id}
                firstLabel={firstLabel}
                secondLabel={secondLabel}
            />
        ),
        id,
    });
};

const openDoubleSelectionOrCreateInputModal = (
    title: string,
    firstData: ComboboxData,
    secondData: ComboboxData | ((firstValue: string | null) => ComboboxData),
    firstLabel: string,
    secondLabel: string,
    searchable: boolean,
    onSelected: (firstValue: string | null, secondValue: string | null) => void,
    onCreated: (firstValue: string, secondValue: string | null) => void,
    onCancel?: () => void,
    width?: number
) => {
    const id = randomId();

    const tabs = () => {
        return (
            <Tabs defaultValue={'select'}>
                <Tabs.List>
                    <Tabs.Tab value={'select'}>Select</Tabs.Tab>
                    <Tabs.Tab value={'create'}>Create</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value={'select'}>
                    <DoubleSelectionInputContent
                        width={width}
                        onConfirm={onSelected}
                        onCancel={onCancel}
                        id={id}
                        searchable={searchable}
                        firstLabel={firstLabel}
                        secondLabel={secondLabel}
                        firstData={firstData}
                        secondData={secondData}
                    />
                </Tabs.Panel>
                <Tabs.Panel value={'create'}>
                    <DoubleStringInputContent
                        width={width}
                        onConfirm={onCreated}
                        onCancel={onCancel}
                        id={id}
                        firstLabel={firstLabel}
                        secondLabel={secondLabel}
                    />
                </Tabs.Panel>
            </Tabs>
        );
    };

    openContentModal({ title, content: tabs(), id });
};

const openSeveritySelectInputModal = <T,>(
    title: string,
    onConfirm: (values: Array<ValidationEntrySeverity>) => void,
    onCancel?: () => void,
    resourceValidationResults?: Array<T | undefined>,
    getFilter?: (
        value: T | undefined,
        validationEntrySeverity: ValidationEntrySeverity
    ) => boolean,
    width?: number
) => {
    const id = randomId();
    const SeveritySelectInputContent = ({
        onConfirm,
        onCancel,
        id,
    }: {
        onConfirm: (values: Array<ValidationEntrySeverity>) => void;
        onCancel?: () => void;
        id: string;
    }) => {
        const getSumOfMaxSeverity = (option: ValidationEntrySeverity) => {
            if (resourceValidationResults && getFilter) {
                return resourceValidationResults.filter((value) =>
                    getFilter(value, option)
                ).length;
            }
            return '';
        };

        const [valueState, setValueState] = useState<
            Array<ValidationEntrySeverity>
        >([]);

        const handleConfirm = () => {
            onConfirm(valueState);
            modals.close(id);
        };

        const handleCancel = () => {
            setValueState([]);
            if (onCancel) {
                onCancel();
            }
        };

        const handleToggle = (severity: ValidationEntrySeverity) => {
            setValueState((prev) =>
                prev.includes(severity) ?
                    prev.filter((s) => s !== severity)
                    :   [...prev, severity]
            );
        };

        return (
            <Stack
                miw={width ?? 400}
                onKeyUp={(event) => {
                    if (event.key === 'Enter') {
                        handleConfirm();
                    }
                }}
            >
                {Object.values(ValidationEntrySeverity).map((severity) => (
                    <Checkbox
                        key={severity}
                        label={`${severity} (${getSumOfMaxSeverity(severity)})`}
                        checked={valueState.includes(severity)}
                        onChange={() => handleToggle(severity)}
                    />
                ))}
                <Group justify="flex-end">
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button onClick={handleConfirm}>Confirm</Button>
                </Group>
            </Stack>
        );
    };

    openContentModal({
        title,
        content: (
            <SeveritySelectInputContent
                onConfirm={onConfirm}
                onCancel={onCancel}
                id={id}
            />
        ),
        id,
    });
};

export const OpenModal = {
    ContentModal: openContentModal,
    AlertModal: openAlertModal,
    UnsavedChangesModal: openUnsavedChangesAlertModal,
    StringInputModal: openStringInputModal,
    EnumInputModal: openEnumInputModal,
    SelectionInputModal: openSelectionInputModal,
    SelectionInputWithBooleanModal: openSelectionInputWithBooleanModal,
    SeveritySelectionModal: openSeveritySelectInputModal,
    SelectionOrCreateModal: openSelectionOrCreateInputModal,
    DoubleSelectionOrCreateModal: openDoubleSelectionOrCreateInputModal,
    DoubleStringCreateModal: openDoubleInputModal,
};
